import React from 'react';
import { useTranslation } from 'react-i18next';
import { HiUser, HiPhone, HiMail, HiLocationMarker, HiCalendar, HiCurrencyDollar, HiIdentification, HiClipboardList } from 'react-icons/hi';
import { Supplier } from '@/types/supplier';
import { formatDate } from '@/utils/safe-formatting';

interface SupplierInfoTabProps {
  supplier: Supplier | null;
  loading: boolean;
}

const SupplierInfoTab: React.FC<SupplierInfoTabProps> = ({
  supplier,
  loading,
}) => {
  const { t } = useTranslation();

  // Format number as integer with thousands separated by dots
  const formatInteger = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('vi-VN', {
      style: 'decimal',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Remove local formatDate function since we're using the safe one from utils

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: t('suppliers.status.active') },
      inactive: { color: 'bg-red-100 text-red-800', label: t('suppliers.status.inactive') },
      pending: { color: 'bg-yellow-100 text-yellow-800', label: t('suppliers.status.pending') },
      blocked: { color: 'bg-gray-100 text-gray-800', label: t('suppliers.status.blocked') },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{t('suppliers.messages.noSupplierSelected')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          {t('suppliers.tabs.basicInfo')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center space-x-3">
            <HiIdentification className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.code}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.code')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiUser className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.name}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.name')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiPhone className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.phone}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.phone')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiMail className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.email}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.email')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiLocationMarker className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.address}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.address')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiUser className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{supplier.contactPerson || '-'}</p>
              <p className="text-sm text-gray-500">{t('suppliers.detailFields.contactPerson')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-lg font-medium text-gray-900 mb-4">
          {t('suppliers.tabs.additionalInfo')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {supplier.taxId && (
            <div className="flex items-center space-x-3">
              <HiIdentification className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">{supplier.taxId}</p>
                <p className="text-sm text-gray-500">{t('suppliers.fields.taxId')}</p>
              </div>
            </div>
          )}
          <div className="flex items-center space-x-3">
            <HiCalendar className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{formatDate(supplier.createdAt)}</p>
              <p className="text-sm text-gray-500">{t('suppliers.detailFields.createdDate')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <HiCurrencyDollar className="h-5 w-5 text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{formatInteger(supplier.totalPurchase || 0)}</p>
              <p className="text-sm text-gray-500">{t('suppliers.fields.totalPurchase')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-5 w-5 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-gray-400"></div>
            </div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {getStatusBadge(supplier.status)}
              </div>
              <p className="text-sm text-gray-500">{t('suppliers.fields.status')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {(supplier.note || supplier.notes) && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">
            {t('suppliers.fields.note')}
          </h4>
          <div className="flex items-start space-x-3">
            <HiClipboardList className="h-5 w-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm text-gray-900">{supplier.note || supplier.notes || '-'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierInfoTab;
